import {useEffect, useMemo, useRef, type CSSProperties, type MouseEvent, type ReactNode} from 'react';
import {tableFeatures, useTable, type ColumnDef} from '@tanstack/react-table';
import {useVirtualizer} from '@tanstack/react-virtual';
import {Table, TableBody, TableCell, TableHead, TableHeader, TableRow} from '@/components/ui/table';
import {Button} from '@/components/app/button';
import {ChevronRight} from 'lucide-react';
import type {ExpandedNodes, HierarchyNode} from './hierarchy';
import {cn} from '@/lib/utils';

export interface VisibleRange {
  startIndex: number;
  endIndex: number;
}
export interface GridColumn<T> {
  key?: string;
  header?: ReactNode;
  render?: (row: T) => ReactNode;
  className?: string;
  headerClassName?: string;
  style?: CSSProperties;
  expander?: boolean;
}
export interface DataGridProps<T> {
  rows?: T[];
  columns: GridColumn<T>[];
  className?: string;
  rowClassName?: string | ((row: T) => string | Record<string, boolean>);
  getRowId?: string | ((row: T) => string | number);
  onRowAction?: (row: T, event: MouseEvent<HTMLTableRowElement>) => void;
  virtual?: {itemHeight?: number; onRangeChange?: (range: VisibleRange) => void};
  height?: string;
  size?: string;
  striped?: boolean;
  emptyMessage?: ReactNode;
}
const features = tableFeatures({});
const emptyRows: never[] = [];
function readField(row: unknown, key?: string): ReactNode {
  return key?.split('.').reduce<any>((value, field) => value?.[field], row) ?? null;
}
export function DataGrid<T extends object>({
  rows = emptyRows,
  columns,
  className,
  getRowId,
  onRowAction,
  rowClassName,
  virtual,
  height,
  size,
  striped,
  emptyMessage = 'No records found.',
}: DataGridProps<T>) {
  const columnDefs = useMemo<ColumnDef<typeof features, T>[]>(
    () =>
      columns.map((column, index) => ({
        id: column.key ?? String(index),
        accessorFn: (row: T) => readField(row, column.key),
      })),
    [columns],
  );
  const table = useTable({
    features,
    columns: columnDefs,
    data: rows,
    getRowId: (row, index) =>
      String(typeof getRowId === 'function' ? getRowId(row) : getRowId ? (readField(row, getRowId) ?? index) : index),
  });
  const model = table.getRowModel().rows;
  const scroll = useRef<HTMLDivElement>(null);
  const list = useVirtualizer({
    count: model.length,
    getScrollElement: () => scroll.current,
    estimateSize: () => virtual?.itemHeight ?? 42,
    overscan: 6,
    useAnimationFrameWithResizeObserver: true,
    enabled: !!virtual,
  });
  const rangeCallback = useRef(virtual?.onRangeChange);
  rangeCallback.current = virtual?.onRangeChange;
  const items = list.getVirtualItems();
  const start = items[0]?.index;
  const end = items.at(-1)?.index;
  useEffect(() => {
    if (start !== undefined && end !== undefined) rangeCallback.current?.({startIndex: start, endIndex: end + 1});
  }, [start, end, rows.length]);
  const visible = virtual
    ? items.map((item) => ({row: model[item.index], item}))
    : model.map((row) => ({row, item: undefined}));
  return (
    <div
      ref={scroll}
      className={cn('min-h-0 overflow-auto rounded-md border', className)}
      style={height ? {height} : undefined}
    >
      <Table containerClassName="overflow-visible">
        <TableHeader className="sticky top-0 z-10 bg-muted">
          <TableRow>
            {columns.map((column, index) => (
              <TableHead key={index} className={column.headerClassName} style={column.style}>
                {column.header}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {virtual && items.length > 0 && items[0].start > 0 && (
            <TableRow aria-hidden>
              <TableCell colSpan={columns.length} className="p-0" style={{height: items[0].start}} />
            </TableRow>
          )}
          {visible.map(({row, item}, index) => (
            <TableRow
              key={row.id}
              data-index={item?.index}
              ref={item ? list.measureElement : undefined}
              className={cn(
                striped && index % 2 && 'bg-muted/40',
                typeof rowClassName === 'function' ? rowClassName(row.original) : rowClassName,
                onRowAction && 'cursor-pointer',
              )}
              onClick={(event) => onRowAction?.(row.original, event)}
              onKeyDown={(event) => {
                if (event.target === event.currentTarget && (event.key === 'Enter' || event.key === ' ')) {
                  event.preventDefault();
                  event.currentTarget.click();
                }
              }}
              tabIndex={onRowAction ? 0 : undefined}
            >
              {columns.map((column, colIndex) => (
                <TableCell
                  key={colIndex}
                  className={cn('whitespace-normal', size === 'small' && 'p-2', column.className)}
                  style={column.style}
                >
                  {column.render ? column.render(row.original) : readField(row.original, column.key)}
                </TableCell>
              ))}
            </TableRow>
          ))}
          {virtual && items.length > 0 && (
            <TableRow aria-hidden>
              <TableCell
                colSpan={columns.length}
                className="p-0"
                style={{height: Math.max(0, list.getTotalSize() - items.at(-1)!.end)}}
              />
            </TableRow>
          )}
          {!rows.length && (
            <TableRow>
              <TableCell colSpan={columns.length} className="h-20 text-center text-muted-foreground">
                {emptyMessage}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
}
export function filterHierarchy<T>(
  nodes: HierarchyNode<T>[],
  query: string,
  getText: (node: HierarchyNode<T>) => string,
): HierarchyNode<T>[] {
  if (!query.trim()) return nodes;
  const matcher = query.toLocaleLowerCase();
  return nodes.flatMap((node) => {
    const children = filterHierarchy(node.children ?? [], query, getText);
    return getText(node).toLocaleLowerCase().includes(matcher) || children.length ? [{...node, children}] : [];
  });
}
export function HierarchyTable<T>({
  nodes = [],
  columns,
  expanded = {},
  onExpandedChange,
  filter = '',
  onRowAction,
  ...props
}: Omit<DataGridProps<HierarchyNode<T>>, 'rows'> & {
  nodes?: HierarchyNode<T>[];
  expanded?: ExpandedNodes;
  onExpandedChange?: (expanded: ExpandedNodes) => void;
  filter?: string;
}) {
  const rows = useMemo(() => {
    const result: (HierarchyNode<T> & {depth: number})[] = [];
    function walk(items: HierarchyNode<T>[], depth = 0) {
      items.forEach((node) => {
        result.push({...node, depth});
        if (filter || expanded[String(node.key)]) walk(node.children ?? [], depth + 1);
      });
    }
    walk(
      filterHierarchy(
        nodes,
        filter,
        (node) => node.label ?? String(readField(node.data, columns.find((column) => column.expander)?.key) ?? ''),
      ),
    );
    return result;
  }, [nodes, expanded, filter, columns]);
  return (
    <DataGrid
      {...props}
      rows={rows}
      getRowId={(node) => String(node.key)}
      onRowAction={onRowAction}
      columns={columns.map((column) =>
        column.expander
          ? {
              ...column,
              render: (node: HierarchyNode<T> & {depth?: number}) => (
                <div className="flex items-center" style={{paddingLeft: (node.depth ?? 0) * 20}}>
                  {node.children?.length ? (
                    <Button
                      type="button"
                      size="icon-sm"
                      variant="ghost"
                      aria-label={`${expanded[String(node.key)] ? 'Collapse' : 'Expand'} ${node.label ?? 'group'}`}
                      onClick={(event) => {
                        event.stopPropagation();
                        onExpandedChange?.({...expanded, [String(node.key)]: !expanded[String(node.key)]});
                      }}
                    >
                      <ChevronRight className={cn('size-4', expanded[String(node.key)] && 'rotate-90')} />
                    </Button>
                  ) : (
                    // Keep labels aligned with expandable rows, like PrimeReact's tree table.
                    <span aria-hidden className="size-8 shrink-0" />
                  )}
                  {column.render?.(node) ?? readField(node.data, column.key)}
                </div>
              ),
            }
          : column,
      )}
    />
  );
}
